class ProductGallery extends HTMLElement {
    constructor() {
        super()
        this.observeSelectedVariantAttribute()
    }

    connectedCallback() {
        this.elements = {
            prevButton: this.querySelector(".prev-button"),
            nextButton: this.querySelector(".next-button"),
        }

        this.options = {
            axis: "x",
            container: "[data-track]",
            // Hidden lifestyle slides (other variants) are excluded from Embla.
            // Re-queried on every reInit().
            slides: "[data-slide]:not([hidden])",
            loop: false,
        }

        this.option_thumbs = {
            containScroll: "keepSnaps",
            dragFree: true,
        }

        this.viewportNodeMainCarousel = this.querySelector(".main-viewport")
        this.viewportNodeThumbCarousel = this.querySelector(
            ".thumbnail-viewport"
        )

        // Single swap-slide containers (hold one image per variant)
        this.variantSlide = this.querySelector("[data-variant-slide]")
        this.variantThumb = this.querySelector("[data-variant-thumb]")

        // Liquid renders the initial variant's lifestyle slides visible.
        this._lifestyleVariantId = this.dataset.initialVariant || null

        this.init()
        this.handleInitialVariantSelection()
        this.addThumbAxisResizeHandler()
        this.watchVariantSources()
    }

    disconnectedCallback() {
        if (this.onWindowResize) {
            window.removeEventListener("resize", this.onWindowResize)
        }
        if (this.onPopState) {
            window.removeEventListener("popstate", this.onPopState)
        }
        if (this._urlPollId) {
            clearInterval(this._urlPollId)
        }
        if (this.form && this.onFormChange) {
            this.form.removeEventListener("change", this.onFormChange)
        }
    }

    // PDP variant sources: the URL ?variant= param (which updates on scent change)
    // and the product form's hidden [name="id"] input. Both call selectVariant.
    watchVariantSources() {
        const readUrlVariant = () =>
            new URLSearchParams(window.location.search).get("variant")

        this._lastVariantId = readUrlVariant()

        const applyFromUrl = () => {
            const id = readUrlVariant()
            if (id && id !== this._lastVariantId) {
                this._lastVariantId = id
                this.selectVariant(id)
            }
        }

        // Fires on back/forward and on history.pushState-based variant changes
        this.onPopState = applyFromUrl
        window.addEventListener("popstate", this.onPopState)

        // Many themes update ?variant via pushState without a popstate event, so poll.
        this._urlPollId = setInterval(applyFromUrl, 300)

        // Also listen to the product form's variant input if present.
        this.form =
            this.closest('form[action*="/cart/add"]') ||
            document.querySelector('form[action*="/cart/add"]')
        if (this.form) {
            this.onFormChange = () => {
                const input = this.form.querySelector('[name="id"]')
                if (
                    input &&
                    input.value &&
                    input.value !== this._lastVariantId
                ) {
                    this._lastVariantId = input.value
                    this.selectVariant(input.value)
                }
            }
            this.form.addEventListener("change", this.onFormChange)
        }

        // Apply once on load in case the URL already has a variant.
        if (this._lastVariantId) this.selectVariant(this._lastVariantId)
    }

    getThumbAxis() {
        return window.innerWidth >= 1024 ? "y" : "x"
    }

    getThumbOptions() {
        return {
            ...this.options,
            ...this.option_thumbs,
            axis: this.getThumbAxis(),
        }
    }

    addThumbAxisResizeHandler() {
        if (!this.emblaApiThumb) return

        this.lastWindowWidth = window.innerWidth
        this.onWindowResize = () => {
            if (window.innerWidth === this.lastWindowWidth) return
            this.lastWindowWidth = window.innerWidth

            const newAxis = this.getThumbAxis()
            if (newAxis === this.thumbAxis) return
            this.thumbAxis = newAxis

            this.emblaApiThumb.reInit(this.getThumbOptions())
            this.emblaApiThumb.scrollTo(
                this.emblaApiMain.selectedScrollSnap(),
                true
            )
        }

        window.addEventListener("resize", this.onWindowResize)
    }

    observeSelectedVariantAttribute() {
        const closestElement = this.closest(
            "[data-product-card-selected_variant_id]"
        )
        if (!closestElement) return

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (
                    mutation.type === "attributes" &&
                    mutation.attributeName ===
                        "data-product-card-selected_variant_id"
                ) {
                    const newValue = closestElement.getAttribute(
                        "data-product-card-selected_variant_id"
                    )
                    if (newValue) this.selectVariant(newValue)
                }
            }
        })

        observer.observe(closestElement, { attributes: true })
    }

    handleInitialVariantSelection() {
        const closestElement = this.closest(
            "[data-product-card-selected_variant_id]"
        )
        if (!closestElement) {
            console.warn(
                "No ancestor element found with 'data-product-card-selected_variant_id' attribute for initial variant selection."
            )
            // Still swap to the gallery's own initial variant so the slide isn't blank.
            if (this.dataset.initialVariant)
                this.swapVariantImage(this.dataset.initialVariant)
            return
        }

        const initialVariantId = closestElement.getAttribute(
            "data-product-card-selected_variant_id"
        )
        if (initialVariantId) {
            this.selectVariant(initialVariantId)
        } else if (this.dataset.initialVariant) {
            this.swapVariantImage(this.dataset.initialVariant)
        }
    }

    // Swap the variant slide's image + thumb, show that variant's lifestyle
    // slides, then move the carousel to slide 1.
    selectVariant(selectedVariantId) {
        this.swapVariantImage(selectedVariantId)

        if (this.updateLifestyleSlides(selectedVariantId)) {
            this.reInitCarousels()
        }

        // Bring slide 1 (the variant slide) into view on selection.
        this.emblaApiMain?.scrollTo(0, false)
        this.emblaApiThumb?.scrollTo(0, false)
    }

    // Show only the selected variant's lifestyle slides (main + thumbs).
    // Returns true if any slide's visibility changed (i.e. Embla needs reInit).
    updateLifestyleSlides(variantId) {
        const id = String(variantId)
        if (this._lifestyleVariantId === id) return false
        this._lifestyleVariantId = id

        const slides = this.querySelectorAll("[data-lifestyle-for]")
        if (!slides.length) return false

        let changed = false
        slides.forEach((el) => {
            const hide = el.dataset.lifestyleFor !== id
            if (el.hidden !== hide) {
                el.hidden = hide
                changed = true
            }
        })
        return changed
    }

    // Re-query slides (hidden ones are dropped by the slides selector).
    reInitCarousels() {
        this.emblaApiMain?.reInit(this.options)
        this.emblaApiThumb?.reInit(this.getThumbOptions())
    }

    // Cross-fade to the selected variant's image inside slide 1 + its thumb.
    swapVariantImage(variantId) {
        this.swapOne(this.variantSlide, variantId)
        this.swapOne(this.variantThumb, variantId)
    }

    swapOne(container, variantId) {
        if (!container) return
        const items = container.querySelectorAll("[data-variant-id]")
        if (!items.length) return

        let matched = false
        items.forEach((el) => {
            const show = el.dataset.variantId === String(variantId)
            this.setVisible(el, show)
            if (show) matched = true
        })

        // Fallback: variant has no assigned image -> keep first visible, never blank.
        if (!matched) {
            items.forEach((el, i) => this.setVisible(el, i === 0))
        }
    }

    setVisible(el, show) {
        el.classList.toggle("opacity-100", show)
        el.classList.toggle("opacity-0", !show)
        el.classList.toggle("pointer-events-none", !show)
    }

    scrollToSlide(selectedVariantId) {
        const targetSlideIndex =
            this.findSlideIndexByVariantId(selectedVariantId)
        if (targetSlideIndex !== -1) {
            this.emblaApiMain.scrollTo(targetSlideIndex, false)
            if (this.emblaApiThumb) {
                this.emblaApiThumb.scrollTo(targetSlideIndex, false)
            }
        } else {
            // No dedicated per-variant slide (single swap-slide model): the variant
            // image lives in the first slide, so land there.
            this.emblaApiMain.scrollTo(0, false)
            if (this.emblaApiThumb) this.emblaApiThumb.scrollTo(0, false)
        }
    }

    findSlideIndexByVariantId(variantId) {
        return [...this.emblaApiMain.slideNodes()].findIndex(
            (slide) => slide.dataset.targetVariant === variantId
        )
    }

    init() {
        this.emblaApiMain = EmblaCarousel(
            this.viewportNodeMainCarousel,
            this.options
        )

        if (this.viewportNodeThumbCarousel) {
            this.thumbAxis = this.getThumbAxis()
            this.emblaApiThumb = EmblaCarousel(
                this.viewportNodeThumbCarousel,
                this.getThumbOptions()
            )
            const removeThumbHandlers = [
                this.addThumbBtnsClickHandlers(),
                this.addToggleThumbBtnsActive(),
            ]
            this.emblaApiThumb.on("destroy", () =>
                removeThumbHandlers.forEach((remove) => remove())
            )
        }

        const removeHandlers = [
            this.addPrevNextBtnsClickHandlers(
                this.emblaApiMain,
                this.elements.prevButton,
                this.elements.nextButton
            ),
        ]
        this.emblaApiMain.on("destroy", () =>
            removeHandlers.forEach((remove) => remove())
        )
    }

    // Delegated so it survives reInit: the index is looked up from the thumb
    // carousel's CURRENT slide list at click time (hidden slides excluded).
    addThumbBtnsClickHandlers() {
        const onClick = (event) => {
            const slide = event.target.closest("[data-slide]")
            if (!slide) return
            const index = this.emblaApiThumb.slideNodes().indexOf(slide)
            if (index !== -1) this.emblaApiMain.scrollTo(index, false)
        }

        this.viewportNodeThumbCarousel.addEventListener("click", onClick, false)

        return () => {
            this.viewportNodeThumbCarousel.removeEventListener(
                "click",
                onClick,
                false
            )
        }
    }

    // Reads slideNodes() fresh on every call so it stays correct after reInit.
    addToggleThumbBtnsActive() {
        const allThumbs = () =>
            this.viewportNodeThumbCarousel.querySelectorAll("[data-slide]")

        const toggleThumbBtnsState = () => {
            const selected = this.emblaApiMain.selectedScrollSnap()
            const activeNode = this.emblaApiThumb.slideNodes()[selected]
            this.emblaApiThumb.scrollTo(selected)
            // Clear across ALL thumbs (incl. hidden) so no stale 'selected' remains.
            allThumbs().forEach((slide) =>
                slide.classList.toggle("selected", slide === activeNode)
            )
        }

        this.emblaApiMain
            .on("select", toggleThumbBtnsState)
            .on("reInit", toggleThumbBtnsState)
        this.emblaApiThumb
            .on("init", toggleThumbBtnsState)
            .on("reInit", toggleThumbBtnsState)

        return () => {
            this.emblaApiMain
                .off("select", toggleThumbBtnsState)
                .off("reInit", toggleThumbBtnsState)
            allThumbs().forEach((slide) => slide.classList.remove("selected"))
        }
    }

    addPrevNextBtnsClickHandlers(emblaApi, prevBtn, nextBtn) {
        const scrollPrev = () => emblaApi.scrollPrev()
        const scrollNext = () => emblaApi.scrollNext()

        if (prevBtn) {
            prevBtn.addEventListener("click", scrollPrev, false)
        }

        if (nextBtn) {
            nextBtn.addEventListener("click", scrollNext, false)
        }

        const toggleState = () => {
            if (prevBtn) prevBtn.disabled = !emblaApi.canScrollPrev()
            if (nextBtn) nextBtn.disabled = !emblaApi.canScrollNext()
        }

        // reInit: slide count changes when lifestyle slides swap in/out.
        emblaApi
            .on("select", toggleState)
            .on("init", toggleState)
            .on("reInit", toggleState)

        return () => {
            if (prevBtn) prevBtn.removeEventListener("click", scrollPrev, false)
            if (nextBtn) nextBtn.removeEventListener("click", scrollNext, false)
        }
    }
}

if (!customElements.get("product-media-gallery")) {
    customElements.define("product-media-gallery", ProductGallery)
}
